// The centralized point for configuring application auth
// This provides the necessary setup to request the client configuration from the server
// and otherwise handle login/logout and token management.
// This wraps the react-pkce-oauth2-code library

import z from 'zod';
import React, { useEffect, useState, useContext } from 'react';
// We import this axios for use in the bootstrapping
import axios from 'axios';

// We import this axios so we can mutate the bearer token
import { currentBearerToken, setUnauthCallback } from 'src/services/axios';

import { Overlay } from 'src/common-ui';

import { TAuthConfig, TRefreshTokenExpiredEvent, AuthProvider, AuthContext } from 'react-oauth2-code-pkce';
import { DarkModeEffect } from './darkMode/DarkMode';
import { Logout } from './components/Logout/Logout';
import { Provider } from 'react-redux';
import store from './store';
import { setIsClientFacing } from 'src/services/configuration/AppConfig.slice';

// Provide an empty component for useEffect injecting the current token into the global axios instance
function AxiosShim({ children }: { children: React.ReactNode }): React.ReactElement {
  const { token, idToken, logOut } = useContext(AuthContext);

  useEffect(() => {
    currentBearerToken.next(token);
    // Force a logout if we get unauthorized
    setUnauthCallback(() => logOut());
  }, [token, logOut]);

  useEffect(() => {
    if (!idToken || idToken.length === 0 || currentBearerToken.getValue().length === 0) {
      return;
    }
    // Attempt to post, if it doesn't work, so be it
    axios.post(
      '/asst/api/user/details',
      { id_token: idToken },
      { headers: { Authorization: `Bearer ${currentBearerToken.getValue()}` } },
    );
  }, [idToken]);

  return <>{children}</>;
}

// A login gating function.
// If the user is not logged in then this will explicilty provide the login page
// If the user is logged in then this will render the children
// We put this in the stack so that everything that depends on the token will be guaranteed to have access to
function LoginShim({ children }: { children: React.ReactNode }): React.ReactElement {
  const { token, logIn, loginInProgress } = useContext(AuthContext);
  // loginInProgress is somewhat unreliable. It doesn't seem to propogate across the local storage boundary correctly in all cases.
  // I have observed that if we have 2 tabs opened and are logged out the token appears in the second tab but the loginInProgress doesn't always update
  // If we have a non-empty token and loginInProgress then use the loading
  if (token.length == 0 && loginInProgress) {
    return <Overlay visible={true} fitParent={true} type={'loading'} />;
  }

  if (token.length == 0) {
    return <Logout message={'You are not logged in'} onLogin={() => logIn()} />;
  }

  return <>{children}</>;
}

const openIdConfiguration = z.object({
  authorization_endpoint: z.string(),
  jwks_uri: z.string(),
  token_endpoint: z.string(),
  userinfo_endpoint: z.string(),
  end_session_endpoint: z.string(),
});

export const platformConfig = z.object({
  auth: z.object({
    domain: z.string().nonempty(),
    clientId: z.string().nonempty(),
  }),
  config: z.looseObject({
    isClientFacing: z.boolean().optional().default(true),
  }),
});

interface PlatformConfig extends z.infer<typeof platformConfig> {}
interface OpenIdConfig extends z.infer<typeof openIdConfiguration> {}

async function fetchFrontendConfig(): Promise<PlatformConfig> {
  try {
    const res = await axios.get('/asst/api/config/frontend');
    return platformConfig.parse(res.data);
  } catch (e) {
    if (e instanceof z.ZodError) {
      throw new Error('invalid client config');
    } else if (axios.isAxiosError(e)) {
      throw new Error('error fetching client config');
    }
    throw new Error('unknown error fetching client config');
  }
}

async function fetchOpenIdConfig(frontend: PlatformConfig): Promise<OpenIdConfig> {
  try {
    const res = await axios.get(`${frontend.auth.domain}/.well-known/openid-configuration`);
    return openIdConfiguration.parse(res.data);
  } catch (e) {
    if (e instanceof z.ZodError) {
      throw new Error('invalid identity provider config');
    } else if (axios.isAxiosError(e)) {
      throw new Error('error fetching identity provider config');
    }
    throw new Error('unknown error fetching identity provider config');
  }
}

async function fetchAuthConfig(): Promise<[PlatformConfig, OpenIdConfig]> {
  const frontend = await fetchFrontendConfig();
  const openId = await fetchOpenIdConfig(frontend);
  return [frontend, openId];
}

// initialize global state here to force token injection
const search = new URLSearchParams(window.location.search);
const token = search.get('injectAccessToken');
const tokenExpire = search.get('injectAccessTokenExpire');
if (token && tokenExpire) {
  // This is used in a test. Clear all of local storage to make sure that only the token and tokenExpiration is available
  localStorage.clear();
  // These are all stringify because apparently that is how useBrowserState works as used by ROCP
  localStorage.setItem('ROCP_loginMethod', JSON.stringify('redirect'));
  localStorage.setItem('ROCP_loginInProgress', JSON.stringify(false));
  localStorage.setItem('ROCP_token', JSON.stringify(token));
  localStorage.setItem('ROCP_tokenExpire', JSON.stringify(tokenExpire));
  // A dummy value here to suppress login from the library
  // localStorage.setItem('ROCP_refreshToken', JSON.stringify('client_credentials'));
}

// Wrapper component for setting up the platform.
// This handles
// 1. Fetching the necessary configuration for auth
// 2. Setting up the AuthProvider with the correct configuration
// 3. Providing a login barrier so that the app doesn't render until we've established whether the user is logged in or not
// 4. The dark mode effect, as this is a global concern that we want to ensure is always applied
export function Platform({ children }: { children: React.ReactNode }): React.ReactElement {
  const [authConfig, setAuthConfig] = useState<[PlatformConfig, OpenIdConfig] | null>(null);
  const [initError, setInitError] = useState<string>('');

  useEffect(() => {
    const go = async () => {
      while (true) {
        try {
          const config = await fetchAuthConfig();
          setAuthConfig(config);
          setInitError('');
          break;
        } catch (e) {
          // We've not really got anywhere else to send this
          if (e instanceof Error) {
            setInitError(e.message);
          } else {
            setInitError('unknown error initializing');
          }
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    };
    go();
  }, []);

  useEffect(() => {
    if (initError) {
      // TODO: Display this in the UI
      // eslint-disable-next-line no-console
      console.error('Error initializing auth config', initError);
    }
  }, [initError]);

  useEffect(() => {
    if (authConfig) {
      const isClientFacing = authConfig[0].config?.isClientFacing ?? true;
      store.dispatch(setIsClientFacing(isClientFacing));
    }
  }, [authConfig]);

  if (!authConfig) {
    return (
      <Provider store={store}>
        <DarkModeEffect />
        <Overlay visible={true} fitParent={true} type={initError ? 'error' : 'loading'} text={initError} />
      </Provider>
    );
  }

  // form the input for react-pkce
  const pkceConfig: TAuthConfig = {
    clientId: authConfig[0].auth.clientId,
    authorizationEndpoint: authConfig[1].authorization_endpoint,
    tokenEndpoint: authConfig[1].token_endpoint,
    scope: 'openid profile email offline_access',
    // On refresh token expiration force a login immediately
    onRefreshTokenExpire: (event: TRefreshTokenExpiredEvent) => event.logIn(),
    // Only auto-login when we aren't doing token injection
    autoLogin: !token,
    redirectUri: window.location.origin + window.location.pathname,
  };

  return (
    <Provider store={store}>
      <DarkModeEffect />
      <AuthProvider authConfig={pkceConfig}>
        <LoginShim>
          <AxiosShim>{children}</AxiosShim>
        </LoginShim>
      </AuthProvider>
    </Provider>
  );
}
